import { useState } from 'react';
import { HandsClapping, Briefcase, Check, X, Eye } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import type { FightRequest, SponsorRequest } from '@/types/request';

interface RequestManagerProps {
  fightRequests: FightRequest[];
  sponsorRequests: SponsorRequest[];
  onApproveFightRequest: (requestId: string, notes?: string) => void;
  onRejectFightRequest: (requestId: string, notes?: string) => void;
  onApproveSponsorRequest: (requestId: string, notes?: string) => void;
  onRejectSponsorRequest: (requestId: string, notes?: string) => void;
}

export function RequestManager({
  fightRequests,
  sponsorRequests,
  onApproveFightRequest,
  onRejectFightRequest,
  onApproveSponsorRequest,
  onRejectSponsorRequest,
}: RequestManagerProps) {
  const [selectedFightRequest, setSelectedFightRequest] = useState<FightRequest | null>(null);
  const [selectedSponsorRequest, setSelectedSponsorRequest] = useState<SponsorRequest | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');

  const pendingFightRequests = fightRequests.filter(r => r.status === 'pending');
  const pendingSponsorRequests = sponsorRequests.filter(r => r.status === 'pending');

  const handleReviewFightRequest = (request: FightRequest, action: 'approve' | 'reject') => {
    setSelectedFightRequest(request);
    setReviewAction(action);
    setShowDialog(true);
  };

  const handleReviewSponsorRequest = (request: SponsorRequest, action: 'approve' | 'reject') => {
    setSelectedSponsorRequest(request);
    setReviewAction(action);
    setShowDialog(true);
  };

  const confirmReview = () => {
    if (selectedFightRequest) {
      if (reviewAction === 'approve') {
        onApproveFightRequest(selectedFightRequest.id, reviewNotes);
        toast.success('Fight request approved');
      } else {
        onRejectFightRequest(selectedFightRequest.id, reviewNotes);
        toast.success('Fight request rejected');
      }
    } else if (selectedSponsorRequest) {
      if (reviewAction === 'approve') {
        onApproveSponsorRequest(selectedSponsorRequest.id, reviewNotes);
        toast.success('Sponsorship request approved');
      } else {
        onRejectSponsorRequest(selectedSponsorRequest.id, reviewNotes);
        toast.success('Sponsorship request rejected');
      }
    }

    setShowDialog(false);
    setSelectedFightRequest(null);
    setSelectedSponsorRequest(null);
    setReviewNotes('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl uppercase text-secondary tracking-wide">
          Request Management
        </h2>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Review and manage fight and sponsorship requests
        </p>
      </div>

      <Tabs defaultValue="fights" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="fights" className="flex items-center gap-2">
            <HandsClapping className="w-4 h-4" />
            Fight Requests ({pendingFightRequests.length})
          </TabsTrigger>
          <TabsTrigger value="sponsors" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Sponsorships ({pendingSponsorRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fights" className="mt-4">
          <ScrollArea className="h-[600px]">
            <div className="flex flex-col gap-3">
              {pendingFightRequests.length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">No pending fight requests</p>
                </Card>
              ) : (
                pendingFightRequests.map(request => (
                  <Card key={request.id} className="p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{request.boxerName}</h3>
                            {request.opponentName && (
                              <>
                                <span className="text-muted-foreground">vs</span>
                                <span className="font-medium">{request.opponentName}</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(request.createdAt)}
                          </p>
                        </div>
                        <Badge variant="outline" className="bg-accent/10">Pending</Badge>
                      </div>
                      
                      <p className="text-sm">{request.message}</p>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleReviewFightRequest(request, 'approve')}
                          className="flex-1 sm:flex-none"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReviewFightRequest(request, 'reject')}
                          className="flex-1 sm:flex-none"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="sponsors" className="mt-4">
          <ScrollArea className="h-[600px]">
            <div className="flex flex-col gap-3">
              {pendingSponsorRequests.length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">No pending sponsorship requests</p>
                </Card>
              ) : (
                pendingSponsorRequests.map(request => (
                  <Card key={request.id} className="p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{request.boxerName}</h3>
                            {request.sponsorName && (
                              <>
                                <span className="text-muted-foreground">→</span>
                                <span className="font-medium">{request.sponsorName}</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(request.createdAt)}
                          </p>
                        </div>
                        <Badge variant="outline" className="bg-accent/10">Pending</Badge>
                      </div>
                      
                      <p className="text-sm">{request.message}</p>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleReviewSponsorRequest(request, 'approve')}
                          className="flex-1 sm:flex-none"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReviewSponsorRequest(request, 'reject')}
                          className="flex-1 sm:flex-none"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">
              {reviewAction === 'approve' ? 'Approve' : 'Reject'} Request
            </DialogTitle>
            <DialogDescription>
              {reviewAction === 'approve' 
                ? 'Add any notes or instructions for the approved request' 
                : 'Provide a reason for rejecting this request'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label htmlFor="notes">{reviewAction === 'approve' ? 'Notes (Optional)' : 'Rejection Reason'}</Label>
            <Textarea
              id="notes"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder={
                reviewAction === 'approve'
                  ? 'e.g., Scheduled for next event on...'
                  : 'e.g., Not enough experience, try again after...'
              }
              className="mt-1 min-h-[100px]"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDialog(false);
              setReviewNotes('');
            }}>
              Cancel
            </Button>
            <Button
              variant={reviewAction === 'approve' ? 'default' : 'destructive'}
              onClick={confirmReview}
            >
              {reviewAction === 'approve' ? <Check className="w-4 h-4 mr-2" /> : <X className="w-4 h-4 mr-2" />}
              {reviewAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
